import {z as zod} from "zod";
import type {DAG_DataClusterInstanceForFrontend} from "~/domainModels/dag/dataClusterInstances";
import type {DAG_DataClusterForFrontend} from "~/domainModels/dag/dataClusters";
import {DAG_DataSourceConnectionStrategy, dataSourceConnectionStrategyDetailsMap} from "~/domainModels/dag/dataSourceConnectionStrategy";
import type {DAG_DataSourceConnectionWorkflow} from "~/domainModels/dag/dataSourceConnectionWorkflow";
import type {DAG_DataSourceInstanceForFrontend} from "~/domainModels/dag/dataSourceInstances";
import {DAG_FieldType, type DAG_Field} from "~/domainModels/dag/fields";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import type {Result} from "@vesta/error-handling-remix/result";
import {dataResult} from "@vesta/error-handling-remix/result";
import {iso8601DateTimeZodValidator, uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import type {ImageDetails} from "@vesta/janus-compatibility-module/utilities/typeDefinitions";
import {imageDetailsValidator as imageDetailsZodValidator} from "@vesta/janus-compatibility-module/utilities/zodValidationPatterns";

export type DAG_DataSource = {
    id: Uuid;
    createdAt: Iso8601DateTime;
    createdBy: Uuid | null;
    updatedAt: Iso8601DateTime;
    updatedBy: Uuid | null;
    status: DAG_DataSourceStatus;
    displayName: string;
    description: string;
    sourceCredentialsSchema: Array<DAG_Field>;
    connectionWorkflow: DAG_DataSourceConnectionWorkflow;
    // Record<string, unknown>
    /**
     * Includes details like attributionWindow. Ideally should not be required by frontend.
     */
    data: string;
    imageId: Uuid;
    companies: Array<Uuid> | null;
    tags: Array<string>;
};

export enum DAG_DataSourceStatus {
    active = "8eee26c5-5df3-438d-b368-5b2880d11753",
    deleted = "1b52f233-8963-4b2e-83e2-d35daf4fcbda",
}

export type DAG_DataSourceForFrontend = {
    id: Uuid;
    displayName: string;
    description: string;
    logo: ImageDetails | null;
    // isSyncAvailable: boolean;
    dataClusters: Array<DAG_DataClusterForFrontend>;
    dataSourceInstances: Array<DAG_DataSourceInstanceForFrontend>;
    connectionStrategy: DAG_DataSourceConnectionStrategy;
    sourceCredentialsSchema: Array<DAG_Field>;
    tags: Array<string>;
};

// TODO: Confirm the behavior of null companyId
/**
 * Returns all dataSources available for the given companyId.
 * If companyId is not provided, all public data sources will be returned.
 */
export async function getDataSources({
    accessToken,
    intellsysBaseUrl,
}: {
    accessToken: string;
    intellsysBaseUrl: string;
    //
}): Promise<Result<Array<DAG_DataSource>>> {
    const dataSourcesResult = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/xolotl/ui/admin/data-source-templates/get-all`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({}),
        },
        zod.array(
            zod
                .object({
                    dataSourceTemplateId: uuidZodValidator,
                    createdAt: iso8601DateTimeZodValidator,
                    createdBy: uuidZodValidator.nullable().catch(null),
                    updatedAt: iso8601DateTimeZodValidator,
                    updatedBy: uuidZodValidator.nullable().catch(null),
                    status: zod.enum(DAG_DataSourceStatus),
                    displayName: zod.string(),
                    sourceCredentialsSchema: zod.array(
                        zod.object({
                            id: uuidZodValidator,
                            attributeName: zod.string(),
                            attributeType: zod.enum(DAG_FieldType),
                            displayName: zod.string(),
                            description: zod.string().nullable(),
                            nullable: zod.boolean(),
                        }),
                    ),
                    connectionStrategy: zod.enum(DAG_DataSourceConnectionStrategy),
                    imageId: uuidZodValidator,
                    description: zod.string().nullable(),
                    companies: zod.array(uuidZodValidator).nullable(),
                    tags: zod.array(zod.string()),
                    data: zod.string(),
                })
                .transform(
                    (input): DAG_DataSource => ({
                        id: input.dataSourceTemplateId,
                        createdAt: input.createdAt,
                        createdBy: input.createdBy,
                        updatedAt: input.updatedAt,
                        updatedBy: input.updatedBy,
                        status: input.status,
                        displayName: input.displayName,
                        description: input.description ?? "",
                        sourceCredentialsSchema: input.sourceCredentialsSchema.map((field) => ({
                            id: field.id,
                            fieldName: field.attributeName,
                            fieldType: field.attributeType,
                            displayName: field.displayName,
                            description: field.description ?? "",
                            isNullable: field.nullable,
                        })),
                        connectionWorkflow: dataSourceConnectionStrategyDetailsMap[input.connectionStrategy].connectionSteps,
                        imageId: input.imageId,
                        companies: input.companies,
                        tags: input.tags,
                        data: input.data,
                    }),
                ),
        ),
    );
    if (dataSourcesResult.success !== true) {
        throw dataSourcesResult.error;
    }
    const dataSources = dataSourcesResult.data;

    return dataResult(dataSources);
}

// TODO: Please cache this is not optimised and takes lots of time (We are supposed to call this api from everywhere)
// Cache should get invalidated after successfull account creation attempt and data node creation attempt
export async function getDataSourcesForFrontend({
    accessToken,
    intellsysBaseUrl,
    workspaceId,
    filters,
}: {
    accessToken: string;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
    filters?: {
        id?: Uuid;
    };
}): Promise<Result<Array<DAG_DataSourceForFrontend>>> {
    const dataSourcesResult = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/xolotl/ui/data-sources/get-all-combined`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
                "X-Workspace-Id": workspaceId,
            },
            body: JSON.stringify({}),
        },
        zod.array(
            zod
                .object({
                    dataSourceTemplateId: uuidZodValidator,
                    displayName: zod.string(),
                    sourceCredentialsSchema: zod.array(
                        zod.object({
                            id: uuidZodValidator,
                            attributeName: zod.string(),
                            attributeType: zod.enum(DAG_FieldType),
                            displayName: zod.string(),
                            description: zod.string().catch(""),
                            nullable: zod.boolean(),
                        }),
                    ).catch([]),
                    imageDetails: imageDetailsZodValidator.nullable(),
                    description: zod.string().catch(""),
                    dataSourceInstances: zod.array(
                        zod.object({
                            id: uuidZodValidator,
                            accountId: zod.string(),
                            accountName: zod.string(),
                            dataClusters: zod.array(
                                zod.object({
                                    dataClusterTemplateId: uuidZodValidator,
                                    dataClusterId: uuidZodValidator,
                                    displayName: zod.string(),
                                }),
                            ),
                        }),
                    ),
                    clusters: zod.array(
                        zod.object({
                            dataClusterTemplateId: uuidZodValidator,
                            displayName: zod.string(),
                        }),
                    ),
                    connectionStrategy: uuidZodValidator,
                    tags: zod.array(zod.string()),
                })
                .transform(
                    (input): DAG_DataSourceForFrontend => ({
                        id: input.dataSourceTemplateId,
                        displayName: input.displayName,
                        description: input.description,
                        logo: input.imageDetails,
                        dataClusters: input.clusters.map(
                            (cluster): DAG_DataClusterForFrontend => ({
                                id: cluster.dataClusterTemplateId,
                                dataSourceId: input.dataSourceTemplateId,
                                displayName: cluster.displayName,
                            }),
                        ),
                        dataSourceInstances: input.dataSourceInstances.map(
                            (dataSourceInstance): DAG_DataSourceInstanceForFrontend => ({
                                id: dataSourceInstance.id,
                                accountId: dataSourceInstance.accountId,
                                accountName: dataSourceInstance.accountName,
                                dataClustersInstances: dataSourceInstance.dataClusters.map(
                                    (dataClusterInstance): DAG_DataClusterInstanceForFrontend => ({
                                        id: dataClusterInstance.dataClusterId,
                                        nodeTemplateId: dataClusterInstance.dataClusterTemplateId,
                                    }),
                                ),
                            }),
                        ),
                        connectionStrategy: input.connectionStrategy as DAG_DataSourceConnectionStrategy,
                        sourceCredentialsSchema: input.sourceCredentialsSchema.map(
                            (field): DAG_Field => ({
                                id: field.id,
                                fieldName: field.attributeName,
                                fieldType: field.attributeType,
                                displayName: field.displayName,
                                description: field.description,
                                isNullable: field.nullable,
                            }),
                        ),
                        tags: input.tags,
                    }),
                ),
        ),
    );
    if (dataSourcesResult.success !== true) {
        throw dataSourcesResult.error;
    }
    let dataSources = dataSourcesResult.data;

    if (filters !== undefined && filters.id !== undefined) {
        dataSources = dataSources.filter((dataSource) => dataSource.id === filters.id);
    }

    return dataResult(dataSources);
}

export const SHOPIFY_NEW__DATA_SOURCE_ID = "d912982d-81ce-4eb2-b750-45fcbeec95a6" as Uuid;
export const FACEBOOK_PAGE__DATA_SOURCE_ID = "1df33f14-12bb-4c04-bba8-c96a0a0da359" as Uuid;
export const GOOGLE_MY_BUSINESS__DATA_SOURCE_ID = "cc9ce886-c13c-43aa-a3b8-79aa4e581ff6" as Uuid;
export const CUSTOM_CSV_UPLOADER_DATA_SOURCE_ID = "9e385a12-51d4-47bb-82b4-b0b460b0efe2" as Uuid;
export const GAJJU_ADS__DATA_SOURCE_ID = "1d980dab-b561-4eab-9336-4b8640a02afe" as Uuid;
export const GAJJU_ADS__NAME__FIELD_ID = "e4ad9922-6684-48a6-8b14-11e80f343bba" as Uuid;
